/* eslint-disable @typescript-eslint/no-explicit-any */
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe, TitleCasePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  output,
  Output,
  QueryList,
  untracked,
  ViewChild,
  ViewChildren,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSlideToggle, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Observable, Subject, take, takeUntil } from 'rxjs';

import { Environment, ENVIRONMENT } from '@hmax-order/frontend/environment';
import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { normalizeString } from '@hmax-order/shared/format';
import { ActionPillComponent } from '../action-pill/action-pill.component';
import { AcceptedCurrency, Column, TypeData } from '../../interfaces';
import { ActionButtonMenu } from '../buttons';
import { ButtonMenuComponent } from '../buttons/button-menu/button-menu.component';
import {
  CheckboxLabelPipe,
  CheckUnderlinePipe,
  ColumnAlignmentPipe,
  ColumnFormattedValuePipe,
  ColumnValuePipe,
  FormatColumnTextPipe,
  IsTypePipe,
  TooltipTextPipe,
} from '../pipes';
import { CustomPaginatorIntl } from './custom-paginator/custom-paginator';
import { TableService } from './table.service';
import { MatChip } from '@angular/material/chips';
import { TableTagsComponent } from '../table-tags/table-tags.component';
import { ButtonIconSmallComponent } from '../buttons/button-icon-small/button-icon-small.component';

@Component({
  selector: 'hmax-table',
  standalone: true,
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
    ColumnValuePipe,
    TitleCasePipe,
    CapitalizePipe,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
  ],
  imports: [
    ActionPillComponent,
    TableTagsComponent,
    ButtonIconSmallComponent,
    CdkDrag,
    CdkDropList,
    CapitalizePipe,
    CheckboxLabelPipe,
    CheckUnderlinePipe,
    ColumnAlignmentPipe,
    ColumnFormattedValuePipe,
    ColumnValuePipe,
    CommonModule,
    FormatColumnTextPipe,
    IsTypePipe,
    MatButtonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatSlideToggleModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    RouterModule,
    TooltipTextPipe,
    TranslocoModule,
    ButtonMenuComponent,
    MatChip,
  ],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TableComponent implements OnDestroy, AfterViewInit {
  private readonly onDestroy: Subject<void> = new Subject<void>();
  private _env = inject<Environment>(ENVIRONMENT);
  private _liveAnnouncer = inject(LiveAnnouncer);
  private _tableService = inject(TableService);
  private _translocoService = inject(TranslocoService);
  private _datePipe = inject(DatePipe);
  private _titleCasePipe = inject(TitleCasePipe);

  apiUrl = input<string>(this._env.apiUrl);
  columns = input<Column[]>([]);
  showFilter = input<boolean>(true);
  showHeader = input<boolean>(true);
  showPaginator = input<boolean>(true);
  showReload = input<boolean>(false);
  hideBottomBorder = input<boolean>(false);
  enableSort = input<boolean>(true);
  /**
   * F15 — arrastar e soltar LINHAS. Ligado, a tabela emite `rowDropped` com o índice de onde a linha saiu e
   * para onde foi, e quem usa reordena o próprio dado. Enquanto estiver ligado, a busca, a ordenação por
   * cabeçalho e a paginação saem, e o que já estava aplicado delas é desfeito: o índice que o arrasto informa é
   * o da linha na tela, e só com a lista inteira, na ordem em que chegou, ele é o índice do dado.
   */
  reorderRows = input<boolean>(false);
  smallRow = input<boolean>(false);
  labelFilter = input<string>('search');
  currency = input<AcceptedCurrency>('BRL');
  pageSize = input<number>(20);
  editTableButton = input<boolean>(false);
  withModified = input<boolean>(false);
  isSubTable = input<boolean>(false);
  data = input<any>();
  dataObservable = input<Observable<any>>();
  resource = input<string>('');
  total = input<number>();
  exportData = input<boolean>(false);
  exportFileName = input<string>('document');
  onClickRow = input<(row: any) => void>();
  /** Predicado opcional para definir se uma row pode ser selecionada (coluna SELECTION). Quando não informado, todas são selecionáveis */
  rowSelectable = input<(row: any) => boolean>();
  /**
   * Portão opcional do "marcar/desmarcar todos" da coluna TOGGLE: recebe a coluna, o valor que o lote
   * vai aplicar e as rows afetadas, e devolve se a troca pode acontecer. Serve para confirmar ação em
   * massa destrutiva antes de mexer na tela (ex.: desvincular todos apaga o preço no ponto de venda).
   * Sem ele, o lote é aplicado direto.
   */
  selectAllGuard = input<(params: { column: Column; targetValue: boolean; rows: any[] }) => Observable<boolean>>();

  /** Nome do campo no objeto da row que contém o array de filhos */
  expandField = input<string>('');
  /** Colunas da tabela expandida (se não informado, usa as mesmas colunas do pai) */
  expandColumns = input<Column[]>([]);
  /** Cor de fundo da tabela expandida */
  expandBgColor = input<string>('bg-gray-50 dark:bg-gray-800');

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChildren('inputField') inputFields!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild('input') private _searchInput?: ElementRef<HTMLInputElement>;

  @Output() actionClick = new EventEmitter<any>();
  @Output() toggleClick = new EventEmitter<any>();
  @Output() checkboxClick = new EventEmitter<any>();
  @Output() checkboxSelectAllClick = new EventEmitter<{
    column: Column;
    rows: any[];
    allChecked: boolean;
  }>();
  /** Lote do "marcar/desmarcar todos" da coluna TOGGLE: só as rows que mudaram, num evento só */
  @Output() toggleSelectAllClick = new EventEmitter<{
    column: Column;
    rows: any[];
    allChecked: boolean;
  }>();
  @Output() editClick = new EventEmitter<any>();
  @Output() cellClick = new EventEmitter<any>();
  @Output() selectionChange = new EventEmitter<any[]>();
  /** Emite ao dar duplo clique numa row, repassando a row e o MouseEvent (útil para posicionar menus/overlays) */
  @Output() rowDoubleClick = new EventEmitter<{ row: any; event: MouseEvent }>();
  /** F15 — linha solta no arrastar (`reorderRows`): de onde saiu e para onde foi, na lista inteira. */
  readonly rowDropped = output<{ previousIndex: number; currentIndex: number }>();

  /**
   * Tipos que passam pelo `columnFormattedValue`. `NUMBER` está aqui porque o outro ramo, o
   * `formatColumnText`, é de TEXTO e lê o `0` como vazio: a posição gravada como 0 sumia, e o
   * `showZero` que a trazia de volta pintava 0 também onde não há valor.
   */
  typesColumnFormatted = ['DATE', 'DATE4', 'DATETIME', 'DAYMONTHTIME', 'TIME', 'TIMESTAMP', 'CURRENCY', 'FLOAT2', 'FLOAT4', 'NUMBER'];
  dataSource = new MatTableDataSource<any>([]);
  filterCache = '';
  currentPage = 0;
  totalItems = 0;
  selection = new SelectionModel<any>(true, []);
  exportOptions: ActionButtonMenu[] = [
    { label: 'CSV', translate: false, method: 'CSV' },
    { label: 'XLSX', translate: false, method: 'XLSX' },
  ];
  expandedRows = new Set<any>();

  /** Aceita array ou escalar — coluna `TAGS` sobre campo simples vira um chip só, sem quebrar. */
  asArray(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }
    return value === null || value === undefined || value === '' ? [] : [value];
  }

  constructor() {
    effect(() => {
      const staticData = this.data();
      if (staticData && !this.dataObservable() && !this.resource()) {
        const processedData = this.withModified() ? staticData.map((e: any) => ({ ...e, modified: false })) : staticData;
        this.setDataPaginator(processedData);
      }
    });

    // F15 — ao ligar o arrastar de linhas, sai o que muda o índice da tela; ao desligar, a paginação volta.
    effect(() => {
      const reordering = this.reorderRows();
      untracked(() => this.applyReorderMode(reordering));
    });
  }

  get url(): string {
    return `${this.apiUrl()}/${this.resource()}`;
  }

  get columnNames() {
    return this.columns().map((column) => column.name);
  }

  /** Retorna os nomes das colunas visíveis, incluindo a coluna de expand se necessário */
  get columnNamesWithExpand(): string[] {
    const names = this.columns().map((column) => column.name);
    if (this.expandField() && !this.hasInlineExpandControl) {
      return ['expandControl', ...names];
    }
    return names;
  }

  get hasInlineExpandControl(): boolean {
    return this.columns().some((column) => column?.expandOnClick);
  }

  get columnNamesVisible() {
    return this.columns()
      .filter((column) => !column?.hidden)
      .map((column) => column.name);
  }

  get hideBorderBottom(): boolean {
    const firstCol = this.columns()[0];
    return (firstCol && 'subTable' in firstCol) || this.hideBottomBorder();
  }

  /** Colunas usadas na tabela expandida */
  get resolvedExpandColumns(): Column[] {
    return this.expandColumns().length > 0 ? this.expandColumns() : this.columns();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  ngAfterViewInit() {
    this.loadData();
  }

  private translate(text: string): string {
    return this._translocoService.translate(text);
  }

  private loadDataObservable(): void {
    const obs = this.dataObservable();
    if (obs) {
      obs.pipe(takeUntil(this.onDestroy)).subscribe({
        next: (data: any[]) => {
          const processed = this.withModified() ? data?.map((e: any) => ({ ...e, modified: false })) : data;
          this.setDataPaginator(processed);
        },
      });
    }
  }

  private loadDataResource(): void {
    // `page` é 0-based e a API omite o skip quando page=0 — o getDataApi não envia page falsy
    this._tableService
      .getDataApi(this.url, this.currentPage, this.pageSize())
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (response: any) => {
          const processed = this.withModified() ? response?.map((e: any) => ({ ...e, modified: false })) : response;
          this.setDataPaginator(processed);
        },
      });
  }

  /**
   * Verifica se uma row corresponde ao filtro, considerando as colunas visíveis e a tradução dos valores.
   */
  private matchesFilter(row: any, columns: Column[], columnNames: string[], filter: string): boolean {
    return columnNames.some((colName) => {
      const column = columns.find((col) => col.name === colName);
      const value = this.getNestedValue(row, colName);

      if (!value) {
        return false;
      }

      if (Array.isArray(value)) {
        return value.some((value) => {
          const fieldValue = value.toString();
          const stringValue = column?.translate
            ? this._translocoService.translate(fieldValue.toLowerCase()).toLowerCase()
            : fieldValue.toLowerCase();
          return normalizeString(stringValue).includes(filter);
        });
      }

      const fieldValue = value.toString();
      const stringValue = column?.translate
        ? this._translocoService.translate(fieldValue.toLowerCase()).toLowerCase()
        : fieldValue.toLowerCase();

      return normalizeString(stringValue).includes(filter);
    });
  }

  /**
   * Cria filtro personalizado para buscar tanto nos campos principais quanto nos campos
   * das tabelas expandidas, considerando as colunas visíveis e a tradução dos valores.
   */
  private createFilterPredicate() {
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const lowerFilter = normalizeString(filter.trim().toLowerCase());

      if (this.matchesFilter(data, this.columns(), this.columnNamesVisible, lowerFilter)) {
        return true;
      }

      const expandFieldName = this.expandField();
      if (expandFieldName) {
        const children: any[] = data[expandFieldName];
        if (Array.isArray(children)) {
          const expandCols = this.resolvedExpandColumns;
          const expandColNames = expandCols.filter((col) => !col.hidden).map((col) => col.name);
          return children.some((child) => this.matchesFilter(child, expandCols, expandColNames, lowerFilter));
        }
      }

      return false;
    };
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  }

  announceSortChange(sortState: Sort) {
    if (sortState?.direction) {
      this._liveAnnouncer.announce(this.translate(`${sortState.direction}ending-order`));
    } else {
      this._liveAnnouncer.announce(this.translate('clean-order'));
    }
  }

  /**
   * Aplica o filtro de busca, considerando as colunas visíveis e a tradução dos valores.
   */
  applyFilter(event: Event) {
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    this.filterCache = filterValue.trim().toLowerCase();

    if (this.expandField() && filterValue.trim()) {
      this.expandedRows.clear();
      this.dataSource.filteredData.forEach((row) => {
        const matchesParent = this.columnNamesVisible.some((colName) => {
          const value = this.getNestedValue(row, colName);
          return value && normalizeString(value.toString().toLowerCase()).includes(normalizeString(filterValue.trim().toLowerCase()));
        });
        if (!matchesParent) {
          this.expandedRows.add(row);
        }
      });
    } else {
      this.expandedRows.clear();
    }
  }

  loadData(): void {
    if (this.dataObservable()) {
      return this.loadDataObservable();
    }
    if (this.resource()) {
      return this.loadDataResource();
    }
    if (this.data()) {
      const processedData = this.withModified() ? this.data().map((e: any) => ({ ...e, modified: false })) : this.data();
      return this.setDataPaginator(processedData);
    }
  }

  setDataPaginator(data: any) {
    this.totalItems = data?.length ?? 0;
    this.dataSource = new MatTableDataSource<any>(data || []);

    // Arrastando linhas (F15), a lista fica inteira na tela: o índice do arrasto tem de ser o do dado.
    if (this.showPaginator() && this.paginator && !this.reorderRows()) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.paginator.pageSize = this.pageSize();
    }

    this.createFilterPredicate();
    this.dataSource.filter = this.filterCache;
    this.dataSource.sort = this.sort;
  }

  /** F15 — linha solta no arrastar. Solta no mesmo lugar, não há o que avisar. */
  onRowDropped(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex !== event.currentIndex) {
      this.rowDropped.emit({ previousIndex: event.previousIndex, currentIndex: event.currentIndex });
    }
  }

  /**
   * F15 — liga ou desliga o arrastar de linhas (`reorderRows`). Ligando: desfaz a busca, a ordenação por
   * cabeçalho e a paginação, para a tela mostrar o dado inteiro na ordem em que chegou. Desligando: a
   * paginação volta.
   */
  private applyReorderMode(reordering: boolean): void {
    if (!reordering) {
      if (this.showPaginator() && this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
      return;
    }

    this.filterCache = '';
    this.dataSource.filter = '';
    if (this._searchInput) {
      this._searchInput.nativeElement.value = '';
    }
    if (this.sort?.active) {
      this.sort.active = '';
      this.sort.direction = '';
      this.sort.sortChange.emit({ active: '', direction: '' });
    }
    this.dataSource.paginator = null;
  }

  exportTable(action?: ActionButtonMenu): void {
    const dataToExport = this.dataSource.data;
    if (!dataToExport?.length) return;

    const exportRows = dataToExport.map((row) => {
      const obj: any = {};
      this.columns().forEach((col) => {
        if (!col.hidden && col.name !== 'actions' && col.type !== 'SELECTION') {
          const header = this.translate(col.alias || col.name);
          let value = this.getNestedValue(row, col.name);

          if (value) {
            switch (col.type) {
              case 'DATE':
                value = this._datePipe.transform(value, 'dd/MM/yy', col?.timezone);
                break;
              case 'DATE4':
                value = this._datePipe.transform(value, 'dd/MM/yyyy');
                break;
              case 'DATETIME':
                value = this._datePipe.transform(value, 'dd/MM/yy HH:mm');
                break;
              case 'DAYMONTHTIME':
                value = this._datePipe.transform(value, 'dd/MM HH:mm');
                break;
              case 'TIME':
                value = this._datePipe.transform(value, 'HH:mm:ss');
                break;
              case 'TIMESTAMP':
                value = this._datePipe.transform(value, 'dd/MM/yy HH:mm:ss');
                break;
            }
          }

          if (col.translate && value) value = this.translate(value.toString().toLowerCase());
          if (col.titleCase && value) value = this._titleCasePipe.transform(value);

          obj[header] = value;
        }
      });
      return obj;
    });
  }

  doActionClick(method: string, index: number, row: any): void {
    this.actionClick.emit({ method, index, ...row });
  }

  doRowClick(index: number, row: any): void {
    const clickFn = this.onClickRow();
    if (clickFn) {
      clickFn({ index, ...row });
    }
  }

  /** Repassa o duplo clique na row (com o evento) para quem quiser abrir menus/overlays posicionados */
  doubleClick(row: any, event: MouseEvent): void {
    this.rowDoubleClick.emit({ row, event });
  }

  doCellClick(column: Column, index: number, row: any, event?: Event): void {
    if (column?.expandOnClick && this.isExpandable(row)) {
      this.toggleExpand(row, event ?? new Event('click'));
      return;
    }
    if (column?.cellClick) this.cellClick.emit({ index, columnName: column.name, ...row });
  }

  doToggleClick(row: any, column: Column) {
    row[column.name] = !row[column.name];
    if ('modified' in row) row.modified = true;
    this.toggleClick.emit(row);
  }

  /**
   * Trata a alteração de um checkbox individual via evento (change).
   * Inverte o valor, marca a row como modificada (quando aplicável) e emite o evento.
   */
  onCheckboxChange(row: any, column: Column): void {
    row[column.name] = !row[column.name];
    if ('modified' in row) row.modified = true;
    this.checkboxClick.emit(row);
  }

  /** Retorna as rows consideradas pelo "Select All" (respeita o filtro aplicado) */
  private getSelectAllRows(): any[] {
    return this.dataSource?.filteredData ?? [];
  }

  /** Retorna o total de rows e quantas possuem o valor booleano da coluna (CHECKBOX/TOGGLE) marcado */
  private getCheckboxState(column: Column): { total: number; checked: number } {
    const rows = this.getSelectAllRows();
    const checked = rows.filter((row) => !!row[column.name]).length;
    return { total: rows.length, checked };
  }

  /** Verifica se todas as rows possuem o valor booleano da coluna (CHECKBOX/TOGGLE) marcado */
  isAllCheckboxesChecked(column: Column): boolean {
    const { total, checked } = this.getCheckboxState(column);
    return total > 0 && checked === total;
  }

  /** Verifica se ao menos uma row (mas não todas) possui o checkbox marcado (estado indeterminado) */
  isSomeCheckboxChecked(column: Column): boolean {
    const { total, checked } = this.getCheckboxState(column);
    return checked > 0 && checked < total;
  }

  /**
   * Alterna o "Select All" da coluna CHECKBOX:
   * - se todas marcadas, desmarca todas;
   * - caso contrário, marca todas.
   * Marca cada row como modificada (quando aplicável) e emite o evento consolidado.
   */
  toggleSelectAll(column: Column): void {
    const rows = this.getSelectAllRows();
    if (!rows.length) {
      this.checkboxSelectAllClick.emit({ column, rows: [], allChecked: false });
      return;
    }

    const allChecked = !this.isAllCheckboxesChecked(column);

    rows.forEach((row) => {
      row[column.name] = allChecked;
      if ('modified' in row) row.modified = true;
    });

    this.checkboxSelectAllClick.emit({ column, rows, allChecked });
  }

  /**
   * `checked` do TOGGLE de cada row, lido a cada verificação.
   *
   * 🔴 **Não pode passar pelo `columnValue`, que é pipe PURO.** O "marcar todos" muta a row no lugar
   * (`row[column.name] = ...`): a referência não muda, o pipe não reavalia e o binding fica no valor
   * antigo. O dado virava e o toggle da linha continuava como estava — cabeçalho ligado com as linhas
   * desligadas, e vice-versa. O clique manual escondia o defeito, porque ali quem vira o visual é o
   * próprio `mat-slide-toggle`. A coluna CHECKBOX nunca teve isso: ela já lia `row[column.name]` direto.
   */
  isToggleChecked(row: any, column: Column): boolean {
    return this.getNestedValue(row, column.name) === true;
  }

  /**
   * Alterna o "Select All" da coluna TOGGLE do cabeçalho:
   * - se todas as rows (respeitando o filtro) estão ligadas, desliga todas;
   * - caso contrário, liga todas.
   *
   * Só toca as rows cujo valor realmente muda, e o lote sai num evento só (`toggleSelectAllClick`) —
   * **não** no `toggleClick` do clique manual, uma vez por linha. Emitir o clique manual em lote
   * fazia o consumidor rodar a validação de linha 51 vezes num clique, e o diálogo de preços abria um
   * aviso listando o catálogo inteiro antes de o usuário ter chance de salvar. Quem quer validar o
   * lote valida no evento do lote; quem quer barrar antes usa o `selectAllGuard`.
   */
  toggleSelectAllToggles(column: Column, source?: MatSlideToggle): void {
    const targetValue = !this.isAllCheckboxesChecked(column);
    const rows = this.getSelectAllRows().filter((row) => !!row[column.name] !== targetValue);

    if (!rows.length) {
      return this.syncSelectAllToggle(column, source);
    }

    const guard = this.selectAllGuard();
    if (!guard) {
      return this.applySelectAllToggles(column, targetValue, rows, source);
    }

    // Devolve o cabeçalho ao estado real ANTES de esperar a decisão: enquanto a confirmação está
    // aberta nada aconteceu ainda, e um guard que nunca responde (diálogo fechado por fora) deixaria
    // o toggle ligado para sempre descrevendo uma lista intacta.
    this.syncSelectAllToggle(column, source);

    guard({ column, targetValue, rows })
      .pipe(take(1), takeUntil(this.onDestroy))
      .subscribe((allowed) => {
        if (allowed) {
          this.applySelectAllToggles(column, targetValue, rows, source);
          return;
        }
        this.syncSelectAllToggle(column, source);
      });
  }

  /** Aplica o valor do lote nas rows afetadas e publica a alteração num evento só */
  private applySelectAllToggles(column: Column, targetValue: boolean, rows: any[], source?: MatSlideToggle): void {
    rows.forEach((row) => {
      row[column.name] = targetValue;
      if ('modified' in row) row.modified = true;
    });

    this.toggleSelectAllClick.emit({ column, rows, allChecked: targetValue });
    this.syncSelectAllToggle(column, source);
  }

  /**
   * Devolve o toggle do cabeçalho ao estado REAL das rows.
   *
   * O `mat-slide-toggle` vira sozinho no clique, antes de qualquer handler. Quando a troca não
   * acontece (guard recusou, ou nenhuma row mudava), o `[checked]` recalcula para o mesmo valor de
   * antes do clique — o Angular não vê mudança, não reescreve nada, e o cabeçalho fica ligado
   * mentindo sobre uma lista que continua desligada.
   */
  private syncSelectAllToggle(column: Column, source?: MatSlideToggle): void {
    if (source) {
      source.checked = this.isAllCheckboxesChecked(column);
    }
  }

  hasSubTable = (_index: number, row: any) => {
    if (!this.columns()[0] || !('subTable' in this.columns()[0])) {
      return false;
    }
    const column = this.columns()[0]?.subTable?.name ?? '';
    return !!row[column];
  };

  /** Tipos numéricos aceitos por uma coluna INPUT (`inputValueType`) que precisam de sanitização na digitação */
  private readonly numericInputValueTypes: TypeData[] = ['CURRENCY', 'FLOAT2', 'FLOAT4'];

  /** Verifica se a coluna INPUT representa um valor numérico (preço, etc.), a partir de `inputValueType` */
  isNumericInputColumn(column: Column): boolean {
    return column?.type === 'INPUT' && this.numericInputValueTypes.includes(column?.inputValueType as TypeData);
  }

  /** Casas decimais aceitas pela coluna numérica (FLOAT4 aceita 4, CURRENCY/FLOAT2 aceitam 2) */
  private numericDecimalPlaces(column: Column): number {
    return column?.inputValueType === 'FLOAT4' ? 4 : 2;
  }

  /**
   * Bloqueia, na digitação, qualquer caractere que não seja dígito ou um único separador decimal
   * (vírgula ou ponto, sem sinal negativo). Teclas de navegação e atalhos continuam liberadas.
   */
  onNumericInputKeydown(event: KeyboardEvent, column: Column): void {
    if (!this.isNumericInputColumn(column) || event.ctrlKey || event.metaKey || event.altKey) return;

    const navigationKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];
    if (navigationKeys.includes(event.key)) return;

    if (/^[0-9]$/.test(event.key)) return;

    const input = event.target as HTMLInputElement;
    const hasSeparator = input.value.includes('.') || input.value.includes(',');
    if ((event.key === ',' || event.key === '.') && !hasSeparator) return;

    event.preventDefault();
  }

  /**
   * Remove caracteres inválidos, normaliza vírgula para ponto e trunca casas decimais
   * excedentes, mantendo o formato que o consumidor (ex.: `changeRowValue`) já espera.
   */
  private sanitizeNumericValue(rawValue: string, column: Column): string {
    const decimalPlaces = this.numericDecimalPlaces(column);
    const cleaned = String(rawValue ?? '').replace(/[^0-9.,]/g, '');
    const separatorIndex = cleaned.search(/[.,]/);

    if (separatorIndex === -1) {
      return cleaned;
    }

    const integerPart = cleaned.slice(0, separatorIndex).replace(/[.,]/g, '');
    const decimalPart = cleaned
      .slice(separatorIndex + 1)
      .replace(/[.,]/g, '')
      .slice(0, decimalPlaces);

    return decimalPlaces > 0 ? `${integerPart}.${decimalPart}` : integerPart;
  }

  onInputChange(row: any, column: Column, event: any) {
    let newValue = event.target.value;

    if (this.isNumericInputColumn(column)) {
      const sanitized = this.sanitizeNumericValue(newValue, column);
      if (sanitized !== newValue) {
        event.target.value = sanitized;
      }
      newValue = sanitized;
    }

    row[column.name] = newValue;
    if ('modified' in row) row.modified = true;
    this.editClick.emit(row);
  }

  /**
   * Valor exibido na célula numérica: converte o separador decimal armazenado (ponto) para
   * vírgula (pt-BR) e, quando vazio, mostra "0,00"/"0,0000" (conforme casas decimais da coluna)
   * em vez de deixar o campo em branco.
   */
  numericInputDisplayValue(row: any, column: Column): string {
    const raw = row?.[column.name];

    if (raw === null || raw === undefined || raw === '') {
      return (0).toFixed(this.numericDecimalPlaces(column)).replace('.', ',');
    }

    return String(raw).replace('.', ',');
  }

  /** Seleciona todo o conteúdo da célula numérica ao focar, para digitar por cima sem precisar apagar */
  onNumericInputFocus(event: FocusEvent, column: Column): void {
    if (!this.isNumericInputColumn(column)) return;
    (event.target as HTMLInputElement).select();
  }

  /**
   * Ao sair da célula numérica, normaliza o valor para a quantidade de casas decimais da coluna
   * (campo vazio ou inválido vira zero) e atualiza a exibição com vírgula. Só marca a row como
   * modificada quando o valor normalizado realmente difere do valor já armazenado.
   */
  onNumericInputBlur(row: any, column: Column, event: Event): void {
    if (!this.isNumericInputColumn(column)) return;

    const input = event.target as HTMLInputElement;
    const decimalPlaces = this.numericDecimalPlaces(column);
    const previous = row?.[column.name];
    const parsed = parseFloat(
      String(previous ?? '')
        .replace(',', '.')
        .trim(),
    );
    const normalized = (Number.isFinite(parsed) ? parsed : 0).toFixed(decimalPlaces);

    input.value = normalized.replace('.', ',');

    if (String(previous ?? '') === normalized) return;

    row[column.name] = normalized;
    if ('modified' in row) row.modified = true;
    this.editClick.emit(row);
  }

  focusInputAt(rowIndex: number, columnName?: string) {
    if (rowIndex == null || columnName == null) return;
    const input = this.inputFields.find((el) => {
      const rowAttr = el.nativeElement.getAttribute('data-row');
      const colAttr = el.nativeElement.getAttribute('data-column');
      return rowAttr === rowIndex.toString() && colAttr === columnName;
    });
    if (input) input?.nativeElement.focus();
  }

  /** Retorna apenas as rows que podem ser selecionadas, respeitando o predicado rowSelectable */
  private get selectableRows(): any[] {
    const predicate = this.rowSelectable();
    const data = this.dataSource?.data ?? [];
    return predicate ? data.filter((row) => predicate(row)) : data;
  }

  /** Verifica se uma row pode ser selecionada (default: todas selecionáveis) */
  isRowSelectable(row: any): boolean {
    const predicate = this.rowSelectable();
    return predicate ? predicate(row) : true;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.selectionChange.emit(this.selection.selected);
      return;
    }
    this.selection.select(...this.selectableRows);
    this.selectionChange.emit(this.selection.selected);
  }

  toggleSelection(row: any) {
    // Não permitir alternar a seleção de rows desabilitadas
    if (!this.isRowSelectable(row)) {
      return;
    }
    this.selection.toggle(row);
    this.selectionChange.emit(this.selection.selected);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.selectableRows.length;
    return numRows > 0 && numSelected === numRows;
  }

  printPage() {
    window.print();
  }

  /** Verifica se a row possui dados expandíveis */
  isExpandable(row: any): boolean {
    const field = this.expandField();
    if (!field) return false;
    const children = row[field];
    return Array.isArray(children) && children.length >= 1;
  }

  /** Alterna o estado de expansão de uma row */
  toggleExpand(row: any, event: Event): void {
    event.stopPropagation();
    if (this.expandedRows.has(row)) {
      this.expandedRows.delete(row);
    } else {
      this.expandedRows.add(row);
    }
  }

  /** Verifica se a row está expandida */
  isExpanded(row: any): boolean {
    return this.expandedRows.has(row);
  }

  /** Retorna os dados filhos de uma row para a tabela expandida */
  getExpandData(row: any): any[] {
    const field = this.expandField();
    if (!field) return [];
    return row[field] || [];
  }
}
